import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import Pagination from "../pagination";
import { cn } from "@/lib/utils"; // utility to join class names
import { ChevronDown, ChevronUp } from "lucide-react";
import { FilterItem } from "@/interfaces";
import { FilterBar } from "../filter-bar";
import { NoData } from "../no-data";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";

export interface ColumnDef<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
  sortable?: boolean;
  sortKey?: string; // key to send to API
  cellClassName?: string;
}

interface SimpleTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  sortKey?: string;
  sortOrder?: "asc" | "desc";
  onSortChange?: (key: string) => void;
  filters: FilterItem[]; // optional filters for the table
  onReset: () => void; // function to reset filters
  onApply?: () => void; // function to apply filters
  container?: boolean;
  getRowClassName?: (row: T) => string; // function to get className for each row
  selectable?: boolean;
  selectedRows?: Set<string>;
  onSelectionChange?: (selected: Set<string>) => void;
  getRowId?: (row: T) => string;
  /**
   * While true the filter bar and header stay mounted (so a focused search
   * input keeps focus). With no rows yet, skeleton rows are shown; with
   * existing rows, they stay visible but dimmed until fresh data lands.
   */
  loading?: boolean;
}

export default function SimpleTable<T>(props: SimpleTableProps<T>) {
  const {
    columns,
    data,
    sortKey,
    sortOrder = "asc",
    onSortChange,
    container = true,
    getRowClassName,
    selectable,
    selectedRows,
    onSelectionChange,
    getRowId,
    loading = false,
  } = props;

  const filterBarProps = { filters: props.filters || [], onReset: props.onReset };
  const paginationProps = {
    page: props.page,
    pageSize: props.pageSize,
    total: props.totalCount,
    setPage: props.setPage,
    setPageSize: props.setPageSize,
  };

  const handleSort = (key?: string) => {
    if (!key || !onSortChange) return;
    onSortChange(key);
  };

  const isSelectable = selectable && selectedRows && onSelectionChange && getRowId;

  const allVisibleIds = isSelectable ? data.map((row) => getRowId(row)) : [];
  const allSelected = isSelectable && allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedRows.has(id));
  const someSelected = isSelectable && allVisibleIds.some((id) => selectedRows.has(id)) && !allSelected;

  const handleSelectAll = () => {
    if (!isSelectable) return;
    if (allSelected) {
      const next = new Set(selectedRows);
      allVisibleIds.forEach((id) => next.delete(id));
      onSelectionChange(next);
    } else {
      const next = new Set(selectedRows);
      allVisibleIds.forEach((id) => next.add(id));
      onSelectionChange(next);
    }
  };

  const handleSelectRow = (rowId: string) => {
    if (!isSelectable) return;
    const next = new Set(selectedRows);
    if (next.has(rowId)) {
      next.delete(rowId);
    } else {
      next.add(rowId);
    }
    onSelectionChange(next);
  };

  const colSpan = columns.length + (isSelectable ? 1 : 0);

  return (
    <div className={!props.container ? "container mx-auto" : ""}>
      {!!props.filters.length && <FilterBar {...filterBarProps} />}

      {/* Main Table */}
      <Table>
        <TableHeader>
          <TableRow>
            {isSelectable && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
            )}
            {columns.map((col, i) => {
              const isActive = sortKey && sortKey === col.sortKey;
              const isSortable = col.sortable && col.sortKey;

              return (
                <TableHead
                  key={i}
                  className={cn(isSortable && "cursor-pointer select-none")}
                  onClick={() => handleSort(col.sortKey)}
                >
                  <div className={cn("flex items-center gap-1", col.className)}>
                    {col.header}
                    {isActive &&
                      (sortOrder === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>

        <TableBody aria-busy={loading} className={cn(loading && data.length > 0 && "opacity-50 transition-opacity")}>
          {loading && data.length === 0
            ? Array.from({ length: Math.min(props.pageSize || 5, 10) }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {isSelectable && (
                    <TableCell className="w-10">
                      <Skeleton className="h-4 w-4" />
                    </TableCell>
                  )}
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-3/4" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
          {!loading && data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="text-center">
                <NoData />
              </TableCell>
            </TableRow>
          ) : null}
          {data.map((row, rowIndex) => {
            const rowId = isSelectable ? getRowId(row) : "";
            const isRowSelected = isSelectable && selectedRows.has(rowId);
            return (
              <TableRow
                key={rowIndex}
                className={cn(
                  getRowClassName ? getRowClassName(row) : undefined,
                  isRowSelected && "bg-muted/50"
                )}
              >
                {isSelectable && (
                  <TableCell className="w-10">
                    <Checkbox
                      checked={isRowSelected}
                      onCheckedChange={() => handleSelectRow(rowId)}
                    />
                  </TableCell>
                )}
                {columns.map((col, colIndex) => {
                  const value =
                    typeof col.accessor === "function" ? col.accessor(row) : (row[col.accessor] as React.ReactNode);
                  return (
                    <TableCell className={col.cellClassName} key={colIndex}>
                      {value}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {!(loading && data.length === 0) && <Pagination {...paginationProps} />}
    </div>
  );
}
